import { UserX } from "lucide-react";
import {
  Button,
  Dialog, DialogContent,
  UserAvatar,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";
import type { UserItem } from "@/features/identity-hub/api/identity-hub.api";
import { useDeactivateUser } from "@/features/identity-hub/hooks/useIdentityHub";
import { ImagePreviewTrigger } from "./ImagePreviewTrigger";
import { DialogIconHeader } from "./shared/dialog/DialogIconHeader";
import { PREVENT_DISMISS_PROPS } from "./shared/dialog/prevent-dismiss-on-outside";

interface SuspendPersonDialogProps {
  user: UserItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeTenantId: string | null;
}

export function SuspendPersonDialog({ user, open, onOpenChange, activeTenantId }: SuspendPersonDialogProps) {
  const { t } = useTranslation();
  const deactivate = useDeactivateUser();

  const handleConfirm = async () => {
    if (!user || !activeTenantId) return;
    try {
      await deactivate.mutateAsync({ userId: user.id, tenantId: activeTenantId });
      onOpenChange(false);
    } catch {
      // onError toast shown by hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex w-full max-w-[420px] flex-col gap-0 overflow-hidden p-0" {...PREVENT_DISMISS_PROPS}>
        <DialogIconHeader
          icon={UserX}
          tone="destructive"
          title={t("iam.suspendDialog.title")}
          description={t("iam.suspendDialog.description", { name: user?.displayName ?? "" })}
        />

        {user && (
          <div className="px-6 py-5">
            <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
              <ImagePreviewTrigger src={user.photoUrl} alt={user.displayName}>
                <UserAvatar name={user.displayName} size="sm" src={user.photoUrl} className="shrink-0" />
              </ImagePreviewTrigger>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{user.displayName}</p>
                <p className="truncate text-xs text-muted-foreground">{user.email}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={deactivate.isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={!user || !activeTenantId || deactivate.isPending}
            onClick={() => void handleConfirm()}
          >
            {deactivate.isPending ? t("iam.suspendDialog.suspending") : t("iam.suspendDialog.confirm")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
